"use client";

import {
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { TABLE, TD, TH, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { MODULO_CALCULATOR_STRINGS } from "@/components/tools/modulo-calculator";
import { t } from "@/lib/i18n/interpolate";
import type {
  Convention,
  ModOperation,
  TableOperation,
} from "@/lib/tool-kit/arithmetic/modulo";
import { modularTable, remainderText } from "@/lib/tool-kit/arithmetic/modulo";
import {
  checkLine,
  type OperationsComputation,
  operationLines,
  type RemainderComputation,
  resultFor,
} from "@/lib/tool-kit/arithmetic/modulo-view";

type Strings = typeof MODULO_CALCULATOR_STRINGS;
type OperationStrings = Strings & {
  symbols: Record<ModOperation, string>;
};

const CONVENTION_NAME = {
  euclidean: "nameEuclidean",
  floored: "nameFloored",
  truncated: "nameTruncated",
} as const;

const CONVENTION_USED = {
  euclidean: "usedEuclidean",
  floored: "usedFloored",
  truncated: "usedTruncated",
} as const;

function display(value: string): string {
  return value.replace("-", "−");
}

export function RemainderResults({
  outcome,
  convention,
  strings,
}: {
  outcome: RemainderComputation;
  convention: Convention;
  strings: Strings;
}) {
  if (outcome.kind === "empty") {
    return <Notice>{strings.enterRemainder}</Notice>;
  }
  if (outcome.kind === "invalid") {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  if (outcome.kind === "zero-modulus") {
    return <ErrorNotice>{strings.zeroModulus}</ErrorNotice>;
  }
  const { view } = outcome;
  const chosen = resultFor(view, convention);
  const rows = view.results.map((r) => [
    strings[CONVENTION_NAME[r.convention]],
    display(r.quotient.toString()),
    remainderText(r),
    checkLine(view, r),
    strings[CONVENTION_USED[r.convention]],
  ]);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.headline, { a: view.aText, n: view.nText })}
          value={remainderText(chosen)}
        />
        <StatCard
          label={strings.quotient}
          value={display(chosen.quotient.toString())}
        />
        <StatCard
          label={strings.divisible}
          value={view.divisible ? strings.yes : strings.no}
        />
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      <DataTable
        head={[
          strings.columnConvention,
          strings.columnQuotient,
          strings.columnRemainder,
          strings.columnCheck,
          strings.columnUsed,
        ]}
        rows={rows}
      />
      <Notice>
        {view.agree
          ? t(strings.agree, { r: remainderText(chosen) })
          : strings.differ}
      </Notice>
    </>
  );
}

export function OperationResults({
  outcome,
  strings,
}: {
  outcome: OperationsComputation;
  strings: OperationStrings;
}) {
  if (outcome.kind === "empty") {
    return <Notice>{strings.enterOperation}</Notice>;
  }
  if (outcome.kind === "invalid") {
    return <ErrorNotice>{strings.invalidWhole}</ErrorNotice>;
  }
  if (outcome.kind === "bad-modulus") {
    return <ErrorNotice>{strings.badModulus}</ErrorNotice>;
  }
  if (outcome.kind === "negative-exponent") {
    return <ErrorNotice>{strings.negativeExponent}</ErrorNotice>;
  }
  if (outcome.kind === "too-big") {
    return <ErrorNotice>{strings.tooBig}</ErrorNotice>;
  }
  const { result } = outcome.view;
  const lines = operationLines(outcome.view, strings);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.operationHeadline, { n: result.n.toString() })}
          value={result.result.toString()}
        />
        {result.raw === null ? null : (
          <StatCard
            label={strings.beforeReducing}
            value={display(result.raw.toString())}
          />
        )}
      </StatGrid>
      <ul className="list-disc space-y-1 pl-6 text-foreground text-sm">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {result.squarings.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.squaringHeading}</h3>
          <p className="text-muted-foreground text-sm">
            {strings.squaringIntro}
          </p>
          <DataTable
            head={[
              strings.columnPower,
              strings.columnValue,
              strings.columnUsedFlag,
            ]}
            rows={result.squarings.map((row) => [
              t(strings.powerCell, {
                a: display(result.a.toString()),
                e: row.exponent.toString(),
              }),
              row.value.toString(),
              row.used ? strings.yes : strings.no,
            ])}
          />
        </>
      ) : null}
    </>
  );
}

export function ModTable({
  n,
  operation,
  strings,
}: {
  n: number;
  operation: TableOperation;
  strings: Strings;
}) {
  const rows = modularTable(n, operation);
  const target = operation === "add" ? 0 : 1;
  const symbol = operation === "add" ? "+" : "×";
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        {t(operation === "add" ? strings.tableAddNote : strings.tableMulNote, {
          n,
        })}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <caption className="sr-only">
            {t(strings.tableCaption, { n, symbol })}
          </caption>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {symbol}
              </th>
              {rows.map((_, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: residues are positions
                <th className={TH} key={j} scope="col">
                  {j}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: residues are positions
              <tr key={i}>
                <th className={TH} scope="row">
                  {i}
                </th>
                {row.map((cell, j) => (
                  <td
                    className={
                      cell === target ? `${TD} bg-primary/10 font-semibold` : TD
                    }
                    // biome-ignore lint/suspicious/noArrayIndexKey: residues are positions
                    key={j}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
